import type {
    AutoValue,
    HideableValue,
    OmitStrict,
    PartiallyRequired,
    RichTextContent,
    Values
} from '@wix/devzai-utils-common';
import {
    arrayRemoveNullishValues,
    autoValueCreate,
    hideableValueCreateHidden,
    HtmlObjectHorizontalPosition,
    HtmlObjectVerticalPosition,
    uuidCreate
} from '@wix/devzai-utils-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import type {DoppeDtoAction} from './doppe-dto-action';
import type {DoppePageLockSettings} from './doppe-page-lock-settings/doppe-page-lock-settings';
import {doppePageLockSettingsGetDefault} from './doppe-page-lock-settings/doppe-page-lock-settings';
import {
    BymoPageActionMediaLayout,
    BymoPageActionMediaSizing,
    BymoPageBackgroundMediaSettings,
    BymoPageHasSearchProps,
    BymoPageHeaderMediaSize,
    BymoPageHeaderMediaViewLayout,
    BymoPageHeaderMediaViewSettings,
    BymoPageProps
} from '../bymo-pages';
import {
    BymoTemplateSpeedDialHeaderAppearance,
    BymoTemplateSpeedDialMenuAppearance,
    BymoTemplateSpeedDialSearchAppearance,
    BymoTemplateSpeedDialSocialLinksAppearance
} from '../../components/bymo-templates/bymo-template-speed-dial/bymo-template-speed-dial-common';
import type {LinkSocialShareSettingInPageProps} from './link-social-share-settings';
import type {PageSEOSettingsProps} from './doppe-dto-bymo-page';
import slugify from 'slugify';
import {DoppeHtmlSitePagesType} from './doppe-html-site';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    type BymoPagePropsContentSettingsMetadata,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsSpecialContentSettingMetadata
} from '../bymo-page-props-metadata/bymo-page-props-metadata';
import {DoppeProductsGridLayoutSize} from './doppe-products-grid-layout-size';
import type {
    DoppeProductListPageCommonProps,
    DoppeProductListPageTemplate,
    DoppeProductListPageTemplateProps
} from './doppe-product-list-page-template/doppe-product-list-page-template';
import {
    doppeProductListPageCommonPropsCreateDefault,
    doppeProductListPageTemplateCreateDefault,
    doppeProductListPageTemplatePropsCreateDefault,
    DoppeProductListPageTemplateId
} from './doppe-product-list-page-template/doppe-product-list-page-template';
import type {
    DoppeProductItemPageCommonProps,
    DoppeProductItemPageTemplate,
    DoppeProductItemPageTemplateProps
} from './doppe-product-item-page-template/doppe-product-item-page-template';
import {
    doppeProductItemPageCommonPropsCreateDefault,
    doppeProductItemPageTemplateCreateDefault,
    doppeProductItemPageTemplatePropsCreateDefault
} from './doppe-product-item-page-template/doppe-product-item-page-template';
import type {
    DoppeServiceListPageCommonProps,
    DoppeServiceListPageTemplate,
    DoppeServiceListPageTemplateProps
} from './doppe-service-list-page-template/doppe-service-list-page-template';
import type {DoppeDtoContactMethod} from './doppe-dto-contact-method';
import {
    DoppeServiceListPageTemplateId,
    doppeServiceListPageCommonPropsCreateDefault,
    doppeServiceListPageTemplateCreateDefault,
    doppeServiceListPageTemplatePropsCreateDefault
} from './doppe-service-list-page-template/doppe-service-list-page-template';
import type {
    DoppeEventListPageCommonProps,
    DoppeEventListPageTemplate,
    DoppeEventListPageTemplateProps
} from './doppe-event-list-page-template/doppe-event-list-page-template';
import {
    doppeEventListPageCommonPropsCreateDefault,
    doppeEventListPageTemplateCreateDefault,
    doppeEventListPageTemplatePropsCreateDefault,
    DoppeEventListPageTemplateId
} from './doppe-event-list-page-template/doppe-event-list-page-template';
import type {
    DoppeEventItemPageCommonProps,
    DoppeEventItemPageTemplate,
    DoppeEventItemPageTemplateProps
} from './doppe-event-item-page-template/doppe-event-item-page-template';
import {
    doppeEventItemPageCommonPropsCreateDefault,
    doppeEventItemPageTemplateCreateDefault,
    doppeEventItemPageTemplatePropsCreateDefault
} from './doppe-event-item-page-template/doppe-event-item-page-template';
import type {
    DoppeTicketingEventItemPageCommonProps,
    DoppeTicketingEventItemPageTemplate,
    DoppeTicketingEventItemPageTemplateProps
} from './doppe-ticketing-event-item-page-template/doppe-ticketing-event-item-page-template';
import {
    doppeTicketingEventItemPageCommonPropsCreateDefault,
    doppeTicketingEventItemPageTemplateCreateDefault,
    doppeTicketingEventItemPageTemplatePropsCreateDefault
} from './doppe-ticketing-event-item-page-template/doppe-ticketing-event-item-page-template';
import type {
    DoppeBlogListPageCommonProps,
    DoppeBlogListPageTemplate,
    DoppeBlogListPageTemplateProps
} from './doppe-blog-list-page-template/doppe-blog-list-page-template';
import {
    doppeBlogListPageCommonPropsCreateDefault,
    doppeBlogListPageTemplateCreateDefault,
    doppeBlogListPageTemplatePropsCreateDefault,
    DoppeBlogListPageTemplateId
} from './doppe-blog-list-page-template/doppe-blog-list-page-template';
import type {
    DoppeBlogItemPageCommonProps,
    DoppeBlogItemPageTemplate,
    DoppeBlogItemPageTemplateProps
} from './doppe-blog-item-page-template/doppe-blog-item-page-template';
import {
    doppeBlogItemPageCommonPropsCreateDefault,
    doppeBlogItemPageTemplateCreateDefault,
    doppeBlogItemPageTemplatePropsCreateDefault
} from './doppe-blog-item-page-template/doppe-blog-item-page-template';
import type {
    DoppeDigitalProductItemPageCommonProps,
    DoppeDigitalProductItemPageTemplate,
    DoppeDigitalProductItemPageTemplateProps
} from './doppe-digital-product-item-page-template/doppe-digital-product-item-page-template';
import {
    doppeDigitalProductItemPageCommonPropsCreateDefault,
    doppeDigitalProductItemPageTemplateCreateDefault,
    doppeDigitalProductItemPageTemplatePropsCreateDefault
} from './doppe-digital-product-item-page-template/doppe-digital-product-item-page-template';
import type {
    DoppeCourseItemPageCommonProps,
    DoppeCourseItemPageTemplate,
    DoppeCourseItemPageTemplateProps
} from './doppe-course-item-page-template/doppe-course-item-page-template';
import {
    doppeCourseItemPageCommonPropsCreateDefault,
    doppeCourseItemPageTemplateCreateDefault,
    doppeCourseItemPageTemplatePropsCreateDefault
} from './doppe-course-item-page-template/doppe-course-item-page-template';
import type {
    DoppeAppointmentItemPageCommonProps,
    DoppeAppointmentItemPageTemplate,
    DoppeAppointmentItemPageTemplateProps
} from './doppe-appointment-item-page-template/doppe-appointment-item-page-template';
import {
    doppeAppointmentItemPageCommonPropsCreateDefault,
    doppeAppointmentItemPageTemplateCreateDefault,
    doppeAppointmentItemPageTemplatePropsCreateDefault
} from './doppe-appointment-item-page-template/doppe-appointment-item-page-template';
import type {
    DoppeClassItemPageCommonProps,
    DoppeClassItemPageTemplate,
    DoppeClassItemPageTemplateProps
} from './doppe-class-item-page-template/doppe-class-item-page-template';
import {
    doppeClassItemPageCommonPropsCreateDefault,
    doppeClassItemPageTemplateCreateDefault,
    doppeClassItemPageTemplatePropsCreateDefault
} from './doppe-class-item-page-template/doppe-class-item-page-template';
import {DoppeInnerPagePresetType} from './doppe-inner-page-preset-type/doppe-inner-page-preset-type';


export const DoppeDtoInnerPageDynamicPagesIds = {
    Product: 'Product',
    DigitalProduct: 'DigitalProduct',
    Event: 'Event',
    TicketingEvent: 'TicketingEvent',
    Course: 'Course',
    Appointment: 'Appointment',
    Class: 'Class',
    BlogPost: 'BlogPost',
} as const;

export type DoppeDtoInnerPageDynamicPagesIds = Values<typeof DoppeDtoInnerPageDynamicPagesIds>;

export const DoppeDtoInnerPageVerticalListPagesIds = {
    Products: 'Products',
    BlogPosts: 'BlogPosts',
    Events: 'Events',
    Services: 'Services',
} as const;

export type DoppeDtoInnerPageVerticalListPagesIds = Values<typeof DoppeDtoInnerPageVerticalListPagesIds>;

export const DoppeDtoInnerPageKnownIds = {
    Home: 'Home',
    Cart: 'Cart',
    ...DoppeDtoInnerPageVerticalListPagesIds,
    ...DoppeDtoInnerPageDynamicPagesIds
} as const;

export type DoppeDtoInnerPageKnownIds = Values<typeof DoppeDtoInnerPageKnownIds>;

// AutoValue marker for "use same media as mobile"
export const DoppeInnerPageDesktopMediaSameAsMobileAutoValue = autoValueCreate<'sameAsMobile'>('sameAsMobile');

/**
 * The source for a desktop inner page main media. Can be:
 * - A WixMediaResource[] for custom media
 * - An AutoValue<'sameAsMobile'> to use the same media as mobile settings
 */
export type DoppeInnerPageDesktopMediaSource = WixMediaResource[] | AutoValue<'sameAsMobile'>;

// Define page types
export const DoppeDtoInnerPageHeaderLayout = {
    Default: 'Default',
    MediaBelow: 'MediaBelow'
} as const;

export type DoppeDtoInnerPageHeaderLayout = Values<typeof DoppeDtoInnerPageHeaderLayout>;

// Header alignment options
export const DoppeDtoInnerPageHeaderAlignment = {
    Start: 'Start',
    Center: 'Center'
} as const;

export type DoppeDtoInnerPageHeaderAlignment = Values<typeof DoppeDtoInnerPageHeaderAlignment>;

// Define page types
export const DoppeDtoInnerPageType = {
    Content: 'Content',
    Products: 'Products',
    Product: 'Product',
    DigitalProduct: 'DigitalProduct',
    BlogPosts: 'BlogPosts',
    BlogPost: 'BlogPost',
    Events: 'Events',
    Event: 'Event',
    TicketingEvent: 'TicketingEvent',
    Services: 'Services',
    Course: 'Course',
    Appointment: 'Appointment',
    Class: 'Class',
    Cart: 'Cart'
} as const;

export type DoppeDtoInnerPageType = Values<typeof DoppeDtoInnerPageType>;

export type DoppeDtoInnerPageVerticalListType =
    | typeof DoppeDtoInnerPageType.Products
    | typeof DoppeDtoInnerPageType.BlogPosts
    | typeof DoppeDtoInnerPageType.Events
    | typeof DoppeDtoInnerPageType.Services

export type DoppeDtoInnerPageVerticalItemType =
    | typeof DoppeDtoInnerPageType.Product
    | typeof DoppeDtoInnerPageType.DigitalProduct
    | typeof DoppeDtoInnerPageType.Course
    | typeof DoppeDtoInnerPageType.Appointment
    | typeof DoppeDtoInnerPageType.Class
    | typeof DoppeDtoInnerPageType.Event
    | typeof DoppeDtoInnerPageType.TicketingEvent
    | typeof DoppeDtoInnerPageType.BlogPost


// export const DoppeDtoPageHomePageId = '38d849f2-6b73-48f8-8168-fccf60ac54d4';

/**
 * Background settings for inner pages.
 * All fields are HideableValue - when hidden, the inner page uses the main page's background settings.
 * When visible (custom), the user can set a custom background for this inner page.
 */
export interface DoppeDtoInnerPageBackgroundSettings {
    /**
     * Content background for mobile.
     * When hidden, uses the main page's backgroundImage.
     * When visible (custom), uses this inner page's specific background.
     */
    backgroundImage: HideableValue<BymoPageBackgroundMediaSettings | null>;

    /**
     * Desktop page background.
     * When hidden, uses the main page's desktopBackgroundImage.
     * When visible (custom), uses this inner page's specific desktop background.
     */
    desktopBackgroundImage: HideableValue<BymoPageBackgroundMediaSettings | null>;

    /**
     * Desktop content background.
     * When hidden, uses the main page's desktopContentBackground.
     * When visible (custom), uses this inner page's specific desktop content background.
     */
    desktopContentBackground: HideableValue<BymoPageBackgroundMediaSettings | null>;
}

export interface DoppeDtoInnerPageHeaderSettings extends BymoPageHasSearchProps, DoppeDtoInnerPageBackgroundSettings {
    mainMedia: WixMediaResource[];
    mainMediaViewSettings: BymoPageHeaderMediaViewSettings;
    mainMediaViewLayout: BymoPageHeaderMediaViewLayout;
    desktopMainMediaSettings: HideableValue<{
        /**
         * Desktop media source. Can be:
         * - WixMediaResource[] for custom media
         * - AutoValue<'sameAsMobile'> to use the same media as mobile settings
         */
        mainMedia: DoppeInnerPageDesktopMediaSource;
        desktopMediaViewSettings: BymoPageHeaderMediaViewSettings;
        desktopMediaViewLayout: BymoPageHeaderMediaViewLayout;
    }>;
    title: HideableValue<string>;
    description: HideableValue<RichTextContent>;
    headerLayout: DoppeDtoInnerPageHeaderLayout;
    headerAlignment: DoppeDtoInnerPageHeaderAlignment;
    headerAppearance: BymoTemplateSpeedDialHeaderAppearance;
    // Main blocks appearance settings for inner pages (flat structure following template pattern)
    socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance;
    menuAppearance: BymoTemplateSpeedDialMenuAppearance;
}

export type DoppeDtoInnerPage =
    | DoppeDtoInnerPage.Content
    | DoppeDtoInnerPage.Products
    | DoppeDtoInnerPage.BlogPosts
    | DoppeDtoInnerPage.BlogPost
    | DoppeDtoInnerPage.Events
    | DoppeDtoInnerPage.Event
    | DoppeDtoInnerPage.TicketingEvent
    | DoppeDtoInnerPage.Product
    | DoppeDtoInnerPage.DigitalProduct
    | DoppeDtoInnerPage.Services
    | DoppeDtoInnerPage.Course
    | DoppeDtoInnerPage.Appointment
    | DoppeDtoInnerPage.Class
    | DoppeDtoInnerPage.Cart

export type DoppeDtoInnerVerticalListPage =
    | DoppeDtoInnerPage.Products
    | DoppeDtoInnerPage.BlogPosts
    | DoppeDtoInnerPage.Events
    | DoppeDtoInnerPage.Services;

export type DoppeDtoInnerVerticalItemPage =
    | DoppeDtoInnerPage.Product
    | DoppeDtoInnerPage.DigitalProduct
    | DoppeDtoInnerPage.Course
    | DoppeDtoInnerPage.Appointment
    | DoppeDtoInnerPage.Class
    | DoppeDtoInnerPage.Event
    | DoppeDtoInnerPage.TicketingEvent
    | DoppeDtoInnerPage.BlogPost

export type DoppeDtoInnerPageWithHeaderSettings = DoppeDtoInnerVerticalListPage | DoppeDtoInnerPage.Content | DoppeDtoInnerPage.Cart


export namespace DoppeDtoInnerPage {
    /**
     * Inner page ID type. Inner pages always have a string ID.
     * Note: The main/home page (which has null ID) is NOT an inner page.
     */
    export type Id = string;

    type BaseType<T extends DoppeDtoInnerPageType> = {
        id: string;
        name: string;
        slug: string;
        type: T;
        lockSettings: DoppePageLockSettings;
    }

    type StaticPageSettings = {
        socialShareSettings: LinkSocialShareSettingInPageProps | null;
        seoSettings: PageSEOSettingsProps | null;
    }

    export type Content = BaseType<typeof DoppeDtoInnerPageType.Content> & StaticPageSettings &
        DoppeDtoInnerPageHeaderSettings & {
        actions: DoppeDtoAction[];
        // Search box content settings for inner pages
        searchBoxHeading: HideableValue<string>;

        boInnerPagePresetType: DoppeInnerPagePresetType | null;
    };

    export type Products = BaseType<typeof DoppeDtoInnerPageType.Products> & StaticPageSettings &
        DoppeDtoInnerPageHeaderSettings & {
        /**
         * Template selector for the product list page.
         * Specifies which template to use for rendering.
         */
        template: DoppeProductListPageTemplate;

        /**
         * Template-specific props for the selected template.
         * The structure depends on the templateId.
         */
        templateProps: DoppeProductListPageTemplateProps;

        /**
         * Common props shared by all templates.
         * Controls shared behavior like showing prices or buttons.
         */
        commonProps: DoppeProductListPageCommonProps;

        /**
         * @deprecated Use templateProps.layoutSize instead when using GridTemplate.
         * Kept for backward compatibility during migration.
         */
        layoutSize?: DoppeProductsGridLayoutSize;
    };

    export type Product = BaseType<typeof DoppeDtoInnerPageType.Product> & {
        /**
         * Template selector for the product item page.
         * Specifies which template to use for rendering.
         */
        template: DoppeProductItemPageTemplate;

        /**
         * Template-specific props for the selected template.
         * The structure depends on the templateId.
         */
        templateProps: DoppeProductItemPageTemplateProps;

        /**
         * Common props shared by all templates.
         * Controls shared behavior like showing buttons or sections.
         */
        commonProps: DoppeProductItemPageCommonProps;
    };

    /**
     * Digital product item page type.
     * Used for digital/downloadable products (no quantity selector, no shipping).
     */
    export type DigitalProduct = BaseType<typeof DoppeDtoInnerPageType.DigitalProduct> & {
        /**
         * Template selector for the digital product item page.
         */
        template: DoppeDigitalProductItemPageTemplate;

        /**
         * Template-specific props for the selected template.
         */
        templateProps: DoppeDigitalProductItemPageTemplateProps;

        /**
         * Common props shared by all templates.
         */
        commonProps: DoppeDigitalProductItemPageCommonProps;
    };


    export type Events = BaseType<typeof DoppeDtoInnerPageType.Events> & StaticPageSettings &
        DoppeDtoInnerPageHeaderSettings & {
        /**
         * Template selector for the events list page.
         */
        template: DoppeEventListPageTemplate;

        /**
         * Template-specific props for the selected template.
         */
        templateProps: DoppeEventListPageTemplateProps;

        /**
         * Common props shared by all templates.
         */
        commonProps: DoppeEventListPageCommonProps;
    };

    export type Event = BaseType<typeof DoppeDtoInnerPageType.Event> & {
        /**
         * Template selector for the event item page (RSVP events).
         */
        template: DoppeEventItemPageTemplate;

        /**
         * Template-specific props for the selected template.
         */
        templateProps: DoppeEventItemPageTemplateProps;

        /**
         * Common props shared by all templates.
         */
        commonProps: DoppeEventItemPageCommonProps;
    };

    /**
     * Ticketing event item page type.
     * Used for events that require ticket purchase (not RSVP).
     */
    export type TicketingEvent = BaseType<typeof DoppeDtoInnerPageType.TicketingEvent> & {
        /**
         * Template selector for the ticketing event item page.
         */
        template: DoppeTicketingEventItemPageTemplate;

        /**
         * Template-specific props for the selected template.
         */
        templateProps: DoppeTicketingEventItemPageTemplateProps;

        /**
         * Common props shared by all templates.
         */
        commonProps: DoppeTicketingEventItemPageCommonProps;
    };

    export type BlogPosts = BaseType<typeof DoppeDtoInnerPageType.BlogPosts> & StaticPageSettings &
        DoppeDtoInnerPageHeaderSettings & {
        /**
         * Template selector for the blog posts list page.
         */
        template: DoppeBlogListPageTemplate;

        /**
         * Template-specific props for the selected template.
         */
        templateProps: DoppeBlogListPageTemplateProps;

        /**
         * Common props shared by all templates.
         */
        commonProps: DoppeBlogListPageCommonProps;
    };

    export type BlogPost = BaseType<typeof DoppeDtoInnerPageType.BlogPost> & {
        /**
         * Template selector for the blog post item page.
         */
        template: DoppeBlogItemPageTemplate;

        /**
         * Template-specific props for the selected template.
         */
        templateProps: DoppeBlogItemPageTemplateProps;

        /**
         * Common props shared by all templates.
         */
        commonProps: DoppeBlogItemPageCommonProps;
    };

    export type Services = BaseType<typeof DoppeDtoInnerPageType.Services> & StaticPageSettings &
        DoppeDtoInnerPageHeaderSettings & {
        /**
         * Template selector for the service list page.
         * Specifies which template to use for rendering.
         */
        template: DoppeServiceListPageTemplate;

        /**
         * Template-specific props for the selected template.
         * The structure depends on the templateId.
         */
        templateProps: DoppeServiceListPageTemplateProps;

        /**
         * Common props shared by all templates.
         * Controls shared behavior like showing prices or buttons.
         */
        commonProps: DoppeServiceListPageCommonProps;

        /**
         * Contact methods shared across all service item pages (appointment, class, course).
         * Stored here so they are configured once and applied to all service detail pages.
         */
        contactMethods: DoppeDtoContactMethod[];
    };

    export type Course = BaseType<typeof DoppeDtoInnerPageType.Course> & {
        /**
         * Template selector for the course item page.
         * Specifies which template to use for rendering.
         */
        template: DoppeCourseItemPageTemplate;

        /**
         * Template-specific props for the selected template.
         * The structure depends on the templateId.
         */
        templateProps: DoppeCourseItemPageTemplateProps;

        /**
         * Common props shared by all templates.
         * Controls shared behavior like showing buttons or sections.
         */
        commonProps: DoppeCourseItemPageCommonProps;
    };

    export type Appointment = BaseType<typeof DoppeDtoInnerPageType.Appointment> & {
        /**
         * Template selector for the appointment item page.
         * Specifies which template to use for rendering.
         */
        template: DoppeAppointmentItemPageTemplate;

        /**
         * Template-specific props for the selected template.
         * The structure depends on the templateId.
         */
        templateProps: DoppeAppointmentItemPageTemplateProps;

        /**
         * Common props shared by all templates.
         * Controls shared behavior like showing buttons or sections.
         */
        commonProps: DoppeAppointmentItemPageCommonProps;
    };

    export type Class = BaseType<typeof DoppeDtoInnerPageType.Class> & {
        /**
         * Template selector for the class item page.
         * Specifies which template to use for rendering.
         */
        template: DoppeClassItemPageTemplate;

        /**
         * Template-specific props for the selected template.
         * The structure depends on the templateId.
         */
        templateProps: DoppeClassItemPageTemplateProps;

        /**
         * Common props shared by all templates.
         * Controls shared behavior like showing buttons or sections.
         */
        commonProps: DoppeClassItemPageCommonProps;
    };

    /**
     * Cart page type.
     * A standalone page for displaying and managing the shopping cart.
     * Extends DoppeDtoInnerPageHeaderSettings for the standard inner page header (media, title, description, alignment).
     * Extends StaticPageSettings for SEO and social share settings (consistent with other vertical pages).
     */
    export type Cart = BaseType<typeof DoppeDtoInnerPageType.Cart> & StaticPageSettings &
        DoppeDtoInnerPageHeaderSettings;
}

export const DoppeDtoInnerPageContentPageContentPropsMetadata = {
    id: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.PageHeading),
    name: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    slug: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    searchBoxHeading: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    searchPlaceholder: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.PageSubheading),
    mainMedia: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
} satisfies BymoPagePropsContentSettingsMetadata<DoppeDtoInnerPage.Content>

/**
 * Derives the vertical page type from a WixTpaAppId
 */
export function doppeDtoInnerPageIsVerticalListType(pageType: DoppeDtoInnerPageType): boolean {
    switch (pageType) {
        case DoppeDtoInnerPageType.Products:
        case DoppeDtoInnerPageType.BlogPosts:
        case DoppeDtoInnerPageType.Events:
        case DoppeDtoInnerPageType.Services:
            return true;
        default:
            return false;
    }
}

/**
 * Derives the vertical page type from a WixTpaAppId
 */
export function doppeDtoInnerPageIsVerticalItemType(pageType: DoppeDtoInnerPageType): boolean {
    switch (pageType) {
        case DoppeDtoInnerPageType.Product:
        case DoppeDtoInnerPageType.DigitalProduct:
        case DoppeDtoInnerPageType.Course:
        case DoppeDtoInnerPageType.Appointment:
        case DoppeDtoInnerPageType.Class:
        case DoppeDtoInnerPageType.Event:
        case DoppeDtoInnerPageType.TicketingEvent:
        case DoppeDtoInnerPageType.BlogPost:
            return true;
        default:
            return false;
    }
}

/**
 * Derives the vertical page type from a WixTpaAppId
 */
export function doppeDtoInnerPageIsContentType(pageType: DoppeDtoInnerPageType): boolean {
    switch (pageType) {
        case DoppeDtoInnerPageType.Content:
            return true;
        default:
            return false;
    }
}

export function doppeDtoInnerPageGetBoPagePresetType(page: DoppeDtoInnerPage.Content): DoppeInnerPagePresetType {
    return page.boInnerPagePresetType ?? DoppeInnerPagePresetType.Unknown;
}

/**
 * Type guard to check if an inner page is a Content page
 */
export function doppeDtoInnerPageIsContent(page: DoppeDtoInnerPage): page is DoppeDtoInnerPage.Content {
    return page.type === DoppeDtoInnerPageType.Content;
}

/**
 * Type guard to check if an inner page is a vertical page (Products, BlogPosts, Events, Bookings)
 */
export function doppeDtoInnerPageIsVerticalList(page: DoppeDtoInnerPage): page is DoppeDtoInnerVerticalListPage {
    return doppeDtoInnerPageIsVerticalListType(page.type);
}

/**
 * Type guard to check if an inner page is a vertical page (Products, BlogPosts, Events, Bookings)
 */
export function doppeDtoInnerPageIsVerticalItem(page: DoppeDtoInnerPage): page is DoppeDtoInnerVerticalItemPage {
    return doppeDtoInnerPageIsVerticalItemType(page.type);
}

/**
 * Creates default media view settings for inner pages
 */
function doppeDtoInnerPageGetDefaultMediaViewSettings(): BymoPageHeaderMediaViewSettings {
    return {
        mediaLayout: BymoPageActionMediaLayout.Cropped,
        mediaSizing: BymoPageActionMediaSizing.Custom,
        size: BymoPageHeaderMediaSize.Ratio3By2,
        alignment: {
            horizontal: HtmlObjectHorizontalPosition.Center,
            vertical: HtmlObjectVerticalPosition.Center
        }
    };
}

/**
 * Creates default desktop main media settings (hidden by default)
 */
export function doppeDtoInnerPageGetDefaultDesktopMainMediaSettings(): HideableValue<{
    mainMedia: DoppeInnerPageDesktopMediaSource;
    desktopMediaViewSettings: BymoPageHeaderMediaViewSettings;
    desktopMediaViewLayout: BymoPageHeaderMediaViewLayout;
}> {
    return hideableValueCreateHidden({
        mainMedia: DoppeInnerPageDesktopMediaSameAsMobileAutoValue,
        desktopMediaViewSettings: doppeDtoInnerPageGetDefaultMediaViewSettings(),
        desktopMediaViewLayout: BymoPageHeaderMediaViewLayout.FullWidth
    });
}

/**
 * Creates default inner page background settings.
 * All settings are hidden by default, meaning the inner page uses the main page's background settings.
 */
export function doppeDtoInnerPageGetDefaultBackgroundSettings(): DoppeDtoInnerPageBackgroundSettings {
    return {
        backgroundImage: hideableValueCreateHidden(null),
        desktopBackgroundImage: hideableValueCreateHidden(null),
        desktopContentBackground: hideableValueCreateHidden(null),
    };
}

export function doppeDtoInnerPageCreateContentPage(
    options: PartiallyRequired<Partial<OmitStrict<DoppeDtoInnerPage.Content, 'type'>>, 'name'>
) {
    const {
        name
    } = options;

    const defaultBackgroundSettings = doppeDtoInnerPageGetDefaultBackgroundSettings();

    return {
        type: DoppeDtoInnerPageType.Content,
        id: options.id ?? uuidCreate(),
        actions: options.actions ?? [],
        mainMedia: options.mainMedia ?? [],
        mainMediaViewSettings: options.mainMediaViewSettings ?? doppeDtoInnerPageGetDefaultMediaViewSettings(),
        mainMediaViewLayout: options.mainMediaViewLayout ?? BymoPageHeaderMediaViewLayout.FullWidth,
        desktopMainMediaSettings: options.desktopMainMediaSettings ?? doppeDtoInnerPageGetDefaultDesktopMainMediaSettings(),
        description: options.description ?? hideableValueCreateHidden(''),
        name: name,
        title: options.title ?? name,
        slug: options.slug ?? slugify(name, {
            lower: true
        }),
        headerLayout: options.headerLayout ?? DoppeDtoInnerPageHeaderLayout.Default,
        headerAlignment: options.headerAlignment ?? DoppeDtoInnerPageHeaderAlignment.Center,
        headerAppearance: options.headerAppearance ?? BymoTemplateSpeedDialHeaderAppearance.Default,
        socialLinksAppearance: options.socialLinksAppearance ?? BymoTemplateSpeedDialSocialLinksAppearance.Hidden,
        menuAppearance: options.menuAppearance ?? BymoTemplateSpeedDialMenuAppearance.Default,
        searchAppearance: options.searchAppearance ?? BymoTemplateSpeedDialSearchAppearance.Hidden,
        searchBoxHeading: options.searchBoxHeading ?? hideableValueCreateHidden(''),
        searchPlaceholder: options.searchPlaceholder ?? hideableValueCreateHidden(''),
        showActionsInSearch: options.showActionsInSearch ?? true,
        showLinksInSearch: options.showLinksInSearch ?? true,
        socialShareSettings: options.socialShareSettings ?? null,
        seoSettings: options.seoSettings ?? null,
        // Background settings - hidden by default (uses main page background)
        backgroundImage: options.backgroundImage ?? defaultBackgroundSettings.backgroundImage,
        desktopBackgroundImage: options.desktopBackgroundImage ?? defaultBackgroundSettings.desktopBackgroundImage,
        desktopContentBackground: options.desktopContentBackground ?? defaultBackgroundSettings.desktopContentBackground,
        boInnerPagePresetType: null,
        lockSettings: doppePageLockSettingsGetDefault(),
    } satisfies DoppeDtoInnerPage.Content;
}

export function doppeDtoInnerPageCreateDefaultProductsPage(
    options?: { templateId?: DoppeProductListPageTemplateId }
) {
    const templateId = options?.templateId ?? DoppeProductListPageTemplateId.FramelessTemplate;
    const defaultBackgroundSettings = doppeDtoInnerPageGetDefaultBackgroundSettings();
    const defaultTemplate = doppeProductListPageTemplateCreateDefault(templateId);

    return {
        id: DoppeDtoInnerPageKnownIds.Products,
        name: 'Products',
        slug: 'products',
        type: DoppeDtoInnerPageType.Products,
        mainMedia: [],
        mainMediaViewSettings: doppeDtoInnerPageGetDefaultMediaViewSettings(),
        mainMediaViewLayout: BymoPageHeaderMediaViewLayout.FullWidth,
        desktopMainMediaSettings: doppeDtoInnerPageGetDefaultDesktopMainMediaSettings(),
        title: 'Products',
        description: '',
        headerLayout: DoppeDtoInnerPageHeaderLayout.Default,
        headerAlignment: DoppeDtoInnerPageHeaderAlignment.Center,
        headerAppearance: BymoTemplateSpeedDialHeaderAppearance.Default,
        socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance.Hidden,
        menuAppearance: BymoTemplateSpeedDialMenuAppearance.Default,
        searchAppearance: BymoTemplateSpeedDialSearchAppearance.Hidden,
        showLinksInSearch: false,
        showActionsInSearch: false,
        searchPlaceholder: '',
        socialShareSettings: null,
        seoSettings: null,
        // Template system
        template: defaultTemplate,
        templateProps: doppeProductListPageTemplatePropsCreateDefault(templateId),
        commonProps: doppeProductListPageCommonPropsCreateDefault(),
        // Background settings - hidden by default (uses main page background)
        ...defaultBackgroundSettings,
        lockSettings: doppePageLockSettingsGetDefault(),
    } as DoppeDtoInnerPage.Products;
}

export function doppeDtoInnerPageCreateBlogPosts(
    options?: { templateId?: DoppeBlogListPageTemplateId }
) {
    const templateId = options?.templateId ?? DoppeBlogListPageTemplateId.FramelessTemplate;
    const defaultBackgroundSettings = doppeDtoInnerPageGetDefaultBackgroundSettings();
    const defaultTemplate = doppeBlogListPageTemplateCreateDefault(templateId);

    return {
        id: DoppeDtoInnerPageKnownIds.BlogPosts,
        name: 'Blog Posts',
        slug: 'blog-posts',
        type: DoppeDtoInnerPageType.BlogPosts,
        mainMedia: [],
        mainMediaViewSettings: doppeDtoInnerPageGetDefaultMediaViewSettings(),
        mainMediaViewLayout: BymoPageHeaderMediaViewLayout.FullWidth,
        desktopMainMediaSettings: doppeDtoInnerPageGetDefaultDesktopMainMediaSettings(),
        title: 'Blog Posts',
        description: '',
        headerLayout: DoppeDtoInnerPageHeaderLayout.Default,
        headerAlignment: DoppeDtoInnerPageHeaderAlignment.Center,
        headerAppearance: BymoTemplateSpeedDialHeaderAppearance.Default,
        socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance.Hidden,
        menuAppearance: BymoTemplateSpeedDialMenuAppearance.Default,
        searchAppearance: BymoTemplateSpeedDialSearchAppearance.Hidden,
        showLinksInSearch: false,
        showActionsInSearch: false,
        searchPlaceholder: '',
        socialShareSettings: null,
        seoSettings: null,
        // Background settings - hidden by default (uses main page background)
        ...defaultBackgroundSettings,
        // Template settings
        template: defaultTemplate,
        templateProps: doppeBlogListPageTemplatePropsCreateDefault(templateId),
        commonProps: doppeBlogListPageCommonPropsCreateDefault(),
        lockSettings: doppePageLockSettingsGetDefault(),
    } as DoppeDtoInnerPage.BlogPosts;
}

export function doppeDtoInnerPageCreateBlogPostPageSettings() {
    const defaultTemplate = doppeBlogItemPageTemplateCreateDefault();

    return {
        id: DoppeDtoInnerPageKnownIds.BlogPost,
        name: 'Blog Post',
        slug: 'blog-post',
        type: DoppeDtoInnerPageType.BlogPost,
        template: defaultTemplate,
        templateProps: doppeBlogItemPageTemplatePropsCreateDefault(defaultTemplate.templateId),
        commonProps: doppeBlogItemPageCommonPropsCreateDefault(),
        lockSettings: doppePageLockSettingsGetDefault(),
    } as DoppeDtoInnerPage.BlogPost;
}

export function doppeDtoInnerPageCreateEvents(
    options?: { templateId?: DoppeEventListPageTemplateId }
) {
    const templateId = options?.templateId ?? DoppeEventListPageTemplateId.FramelessTemplate;
    const defaultBackgroundSettings = doppeDtoInnerPageGetDefaultBackgroundSettings();
    const defaultTemplate = doppeEventListPageTemplateCreateDefault(templateId);

    return {
        id: DoppeDtoInnerPageKnownIds.Events,
        name: 'Events',
        slug: 'events',
        type: DoppeDtoInnerPageType.Events,
        mainMedia: [],
        mainMediaViewSettings: doppeDtoInnerPageGetDefaultMediaViewSettings(),
        mainMediaViewLayout: BymoPageHeaderMediaViewLayout.FullWidth,
        desktopMainMediaSettings: doppeDtoInnerPageGetDefaultDesktopMainMediaSettings(),
        title: 'Events',
        description: '',
        headerLayout: DoppeDtoInnerPageHeaderLayout.Default,
        headerAlignment: DoppeDtoInnerPageHeaderAlignment.Center,
        headerAppearance: BymoTemplateSpeedDialHeaderAppearance.Default,
        socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance.Hidden,
        menuAppearance: BymoTemplateSpeedDialMenuAppearance.Default,
        searchAppearance: BymoTemplateSpeedDialSearchAppearance.Hidden,
        showLinksInSearch: false,
        showActionsInSearch: false,
        searchPlaceholder: '',
        socialShareSettings: null,
        seoSettings: null,
        // Background settings - hidden by default (uses main page background)
        ...defaultBackgroundSettings,
        // Template settings
        template: defaultTemplate,
        templateProps: doppeEventListPageTemplatePropsCreateDefault(templateId),
        commonProps: doppeEventListPageCommonPropsCreateDefault(),
        lockSettings: doppePageLockSettingsGetDefault(),
    } as DoppeDtoInnerPage.Events;
}

export function doppeDtoInnerPageCreateEventPageSettings() {
    const defaultTemplate = doppeEventItemPageTemplateCreateDefault();

    return {
        id: DoppeDtoInnerPageKnownIds.Event,
        name: 'RSVP Event',
        slug: 'rsvp-event',
        type: DoppeDtoInnerPageType.Event,
        template: defaultTemplate,
        templateProps: doppeEventItemPageTemplatePropsCreateDefault(defaultTemplate.templateId),
        commonProps: doppeEventItemPageCommonPropsCreateDefault(),
        lockSettings: doppePageLockSettingsGetDefault(),
    } as DoppeDtoInnerPage.Event;
}

export function doppeDtoInnerPageCreateTicketingEventPageSettings() {
    const defaultTemplate = doppeTicketingEventItemPageTemplateCreateDefault();

    return {
        id: DoppeDtoInnerPageKnownIds.TicketingEvent,
        name: 'Ticket Event',
        slug: 'ticket-event',
        type: DoppeDtoInnerPageType.TicketingEvent,
        template: defaultTemplate,
        templateProps: doppeTicketingEventItemPageTemplatePropsCreateDefault(defaultTemplate.templateId),
        commonProps: doppeTicketingEventItemPageCommonPropsCreateDefault(),
        lockSettings: doppePageLockSettingsGetDefault(),
    } as DoppeDtoInnerPage.TicketingEvent;
}

export function doppeDtoInnerPageCreateProductPageSettings() {
    const defaultTemplate = doppeProductItemPageTemplateCreateDefault();

    return {
        id: DoppeDtoInnerPageKnownIds.Product,
        name: 'Physical Product',
        slug: 'product',
        type: DoppeDtoInnerPageType.Product,
        // Template system
        template: defaultTemplate,
        templateProps: doppeProductItemPageTemplatePropsCreateDefault(defaultTemplate.templateId),
        commonProps: doppeProductItemPageCommonPropsCreateDefault(),
        lockSettings: doppePageLockSettingsGetDefault(),
    } as DoppeDtoInnerPage.Product;
}

export function doppeDtoInnerPageCreateDigitalProductPageSettings() {
    const defaultTemplate = doppeDigitalProductItemPageTemplateCreateDefault();

    return {
        id: DoppeDtoInnerPageKnownIds.DigitalProduct,
        name: 'Digital Product',
        slug: 'digital-product',
        type: DoppeDtoInnerPageType.DigitalProduct,
        // Template system
        template: defaultTemplate,
        templateProps: doppeDigitalProductItemPageTemplatePropsCreateDefault(defaultTemplate.templateId),
        commonProps: doppeDigitalProductItemPageCommonPropsCreateDefault(),
        lockSettings: doppePageLockSettingsGetDefault(),
    } as DoppeDtoInnerPage.DigitalProduct;
}

export function doppeDtoInnerPageCreateDefaultCartPage(): DoppeDtoInnerPage.Cart {
    const defaultBackgroundSettings = doppeDtoInnerPageGetDefaultBackgroundSettings();

    return {
        id: DoppeDtoInnerPageKnownIds.Cart,
        name: 'Cart',
        slug: 'cart',
        type: DoppeDtoInnerPageType.Cart,
        mainMedia: [],
        mainMediaViewSettings: doppeDtoInnerPageGetDefaultMediaViewSettings(),
        mainMediaViewLayout: BymoPageHeaderMediaViewLayout.FullWidth,
        desktopMainMediaSettings: doppeDtoInnerPageGetDefaultDesktopMainMediaSettings(),
        title: 'My Cart',
        description: '',
        headerLayout: DoppeDtoInnerPageHeaderLayout.Default,
        headerAlignment: DoppeDtoInnerPageHeaderAlignment.Center,
        headerAppearance: BymoTemplateSpeedDialHeaderAppearance.Default,
        socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance.Hidden,
        menuAppearance: BymoTemplateSpeedDialMenuAppearance.Default,
        searchAppearance: BymoTemplateSpeedDialSearchAppearance.Hidden,
        showLinksInSearch: false,
        showActionsInSearch: false,
        searchPlaceholder: '',
        socialShareSettings: null,
        seoSettings: null,
        ...defaultBackgroundSettings,
        lockSettings: doppePageLockSettingsGetDefault(),
    };
}

/**
 * Checks if a page is the Cart page by its page ID.
 */
export function doppeDtoInnerPageIsCartPage(pageId: string): boolean {
    return pageId === DoppeDtoInnerPageKnownIds.Cart;
}

export function doppeDtoInnerPageCreateDefaultServicesPage(
    options?: { templateId?: DoppeServiceListPageTemplateId }
) {
    const templateId = options?.templateId ?? DoppeServiceListPageTemplateId.FramelessTemplate;
    const defaultBackgroundSettings = doppeDtoInnerPageGetDefaultBackgroundSettings();
    const defaultTemplate = doppeServiceListPageTemplateCreateDefault(templateId);

    return {
        id: DoppeDtoInnerPageKnownIds.Services,
        name: 'Services',
        slug: 'services',
        type: DoppeDtoInnerPageType.Services,
        mainMedia: [],
        mainMediaViewSettings: doppeDtoInnerPageGetDefaultMediaViewSettings(),
        mainMediaViewLayout: BymoPageHeaderMediaViewLayout.FullWidth,
        desktopMainMediaSettings: doppeDtoInnerPageGetDefaultDesktopMainMediaSettings(),
        title: 'Our Services',
        description: '',
        headerLayout: DoppeDtoInnerPageHeaderLayout.Default,
        headerAlignment: DoppeDtoInnerPageHeaderAlignment.Center,
        headerAppearance: BymoTemplateSpeedDialHeaderAppearance.Default,
        socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance.Hidden,
        menuAppearance: BymoTemplateSpeedDialMenuAppearance.Default,
        searchAppearance: BymoTemplateSpeedDialSearchAppearance.Hidden,
        showLinksInSearch: false,
        showActionsInSearch: false,
        searchPlaceholder: '',
        socialShareSettings: null,
        seoSettings: null,
        // Template system
        template: defaultTemplate,
        templateProps: doppeServiceListPageTemplatePropsCreateDefault(templateId),
        commonProps: doppeServiceListPageCommonPropsCreateDefault(),
        contactMethods: [],
        // Background settings - hidden by default (uses main page background)
        ...defaultBackgroundSettings,
        lockSettings: doppePageLockSettingsGetDefault(),
    } as DoppeDtoInnerPage.Services;
}

export function doppeDtoInnerPageCreateCoursePageSettings() {
    const defaultTemplate = doppeCourseItemPageTemplateCreateDefault();

    return {
        id: DoppeDtoInnerPageKnownIds.Course,
        name: 'Course',
        slug: 'course',
        type: DoppeDtoInnerPageType.Course,
        // Template system
        template: defaultTemplate,
        templateProps: doppeCourseItemPageTemplatePropsCreateDefault(defaultTemplate.templateId),
        commonProps: doppeCourseItemPageCommonPropsCreateDefault(),
        lockSettings: doppePageLockSettingsGetDefault(),
    } as DoppeDtoInnerPage.Course;
}

export function doppeDtoInnerPageCreateAppointmentPageSettings() {
    const defaultTemplate = doppeAppointmentItemPageTemplateCreateDefault();

    return {
        id: DoppeDtoInnerPageKnownIds.Appointment,
        name: 'Appointment',
        slug: 'appointment',
        type: DoppeDtoInnerPageType.Appointment,
        // Template system
        template: defaultTemplate,
        templateProps: doppeAppointmentItemPageTemplatePropsCreateDefault(defaultTemplate.templateId),
        commonProps: doppeAppointmentItemPageCommonPropsCreateDefault(),
        lockSettings: doppePageLockSettingsGetDefault(),
    } as DoppeDtoInnerPage.Appointment;
}

export function doppeDtoInnerPageCreateClassPageSettings() {
    const defaultTemplate = doppeClassItemPageTemplateCreateDefault();

    return {
        id: DoppeDtoInnerPageKnownIds.Class,
        name: 'Class',
        slug: 'class',
        type: DoppeDtoInnerPageType.Class,
        // Template system
        template: defaultTemplate,
        templateProps: doppeClassItemPageTemplatePropsCreateDefault(defaultTemplate.templateId),
        commonProps: doppeClassItemPageCommonPropsCreateDefault(),
        lockSettings: doppePageLockSettingsGetDefault(),
    } as DoppeDtoInnerPage.Class;
}

// ============================================================================
// MIGRATION FUNCTIONS - Deep merge partial settings with defaults
// ============================================================================

/**
 * Supported product list page template IDs.
 * Used for migration to detect deprecated templates.
 */
const SUPPORTED_PRODUCT_LIST_TEMPLATE_IDS = new Set<DoppeProductListPageTemplateId>([
    DoppeProductListPageTemplateId.FramelessTemplate,
    DoppeProductListPageTemplateId.CardTemplate,
    DoppeProductListPageTemplateId.OnImageTemplate,
    DoppeProductListPageTemplateId.RowTemplate,
]);

/**
 * Migrates partial Products page settings by deep merging with defaults.
 * Properly handles nested objects like template, templateProps, commonProps,
 * mainMediaViewSettings, and desktopMainMediaSettings.
 *
 * If the template is a deprecated type (GridTemplate, ListTemplate), it will
 * be migrated to FramelessTemplate.
 */
export function doppeDtoInnerPageMigrateProductsPageFromDefaultValues(
    partialSettings: Partial<DoppeDtoInnerPage.Products> | undefined
): DoppeDtoInnerPage.Products {
    // Determine the user's template ID from stored settings
    const userTemplateId = partialSettings?.template?.templateId;
    const isTemplateSupported = userTemplateId !== undefined &&
        SUPPORTED_PRODUCT_LIST_TEMPLATE_IDS.has(userTemplateId);

    // Create defaults for the user's specific template (or Frameless if unsupported/missing)
    const effectiveTemplateId = isTemplateSupported
        ? userTemplateId
        : DoppeProductListPageTemplateId.FramelessTemplate;
    const defaults = doppeDtoInnerPageCreateDefaultProductsPage({templateId: effectiveTemplateId});

    if (!partialSettings) {
        return defaults;
    }

    const {
        template: templateOverride,
        templateProps: templatePropsOverride,
        commonProps: commonPropsOverride,
        mainMediaViewSettings: mainMediaViewSettingsOverride,
        desktopMainMediaSettings: desktopMainMediaSettingsOverride,
        ...restOverrides
    } = partialSettings;

    // If template is not supported, use defaults (which is FramelessTemplate)
    const migratedTemplate = isTemplateSupported ? {
        ...defaults.template,
        ...templateOverride
    } : defaults.template;

    const migratedTemplateProps = isTemplateSupported ? {
        ...defaults.templateProps,
        ...templatePropsOverride
    } : doppeProductListPageTemplatePropsCreateDefault(DoppeProductListPageTemplateId.FramelessTemplate);

    return {
        ...defaults,
        ...restOverrides,
        template: migratedTemplate,
        templateProps: migratedTemplateProps,
        commonProps: {
            ...defaults.commonProps,
            ...commonPropsOverride
        },
        mainMediaViewSettings: {
            ...defaults.mainMediaViewSettings,
            ...mainMediaViewSettingsOverride
        },
        desktopMainMediaSettings: desktopMainMediaSettingsOverride ?? defaults.desktopMainMediaSettings,
    };
}

/**
 * Supported blog list page template IDs.
 * Used for migration to detect deprecated templates.
 */
const SUPPORTED_BLOG_LIST_TEMPLATE_IDS = new Set<DoppeBlogListPageTemplateId>([
    DoppeBlogListPageTemplateId.FramelessTemplate,
    DoppeBlogListPageTemplateId.CardTemplate,
    DoppeBlogListPageTemplateId.OnImageTemplate,
    DoppeBlogListPageTemplateId.RowTemplate,
]);

/**
 * Migrates partial BlogPosts page settings by deep merging with defaults.
 * Properly handles nested objects like template, templateProps, commonProps,
 * mainMediaViewSettings, and desktopMainMediaSettings.
 *
 * If the template is a deprecated type (GridTemplate, ListTemplate), it will
 * be migrated to FramelessTemplate.
 */
export function doppeDtoInnerPageMigrateBlogPostsPageFromDefaultValues(
    partialSettings: Partial<DoppeDtoInnerPage.BlogPosts> | undefined
): DoppeDtoInnerPage.BlogPosts {
    // Determine the user's template ID from stored settings
    const userTemplateId = partialSettings?.template?.templateId;
    const isTemplateSupported = userTemplateId !== undefined &&
        SUPPORTED_BLOG_LIST_TEMPLATE_IDS.has(userTemplateId);

    // Create defaults for the user's specific template (or Frameless if unsupported/missing)
    const effectiveTemplateId = isTemplateSupported
        ? userTemplateId
        : DoppeBlogListPageTemplateId.FramelessTemplate;
    const defaults = doppeDtoInnerPageCreateBlogPosts({templateId: effectiveTemplateId});

    if (!partialSettings) {
        return defaults;
    }

    const {
        template: templateOverride,
        templateProps: templatePropsOverride,
        commonProps: commonPropsOverride,
        mainMediaViewSettings: mainMediaViewSettingsOverride,
        desktopMainMediaSettings: desktopMainMediaSettingsOverride,
        ...restOverrides
    } = partialSettings;

    // If template is not supported, use defaults (which is FramelessTemplate)
    const migratedTemplate = isTemplateSupported ? {
        ...defaults.template,
        ...templateOverride
    } : defaults.template;

    const migratedTemplateProps = isTemplateSupported ? {
        ...defaults.templateProps,
        ...templatePropsOverride
    } : doppeBlogListPageTemplatePropsCreateDefault(DoppeBlogListPageTemplateId.FramelessTemplate);

    return {
        ...defaults,
        ...restOverrides,
        template: migratedTemplate,
        templateProps: migratedTemplateProps,
        commonProps: {
            ...defaults.commonProps,
            ...commonPropsOverride
        },
        mainMediaViewSettings: {
            ...defaults.mainMediaViewSettings,
            ...mainMediaViewSettingsOverride
        },
        desktopMainMediaSettings: desktopMainMediaSettingsOverride ?? defaults.desktopMainMediaSettings,
    };
}

/**
 * Supported event list page template IDs.
 * Used for migration to detect deprecated templates.
 */
const SUPPORTED_EVENT_LIST_TEMPLATE_IDS = new Set<DoppeEventListPageTemplateId>([
    DoppeEventListPageTemplateId.FramelessTemplate,
    DoppeEventListPageTemplateId.CardTemplate,
    DoppeEventListPageTemplateId.OnImageTemplate,
    DoppeEventListPageTemplateId.RowTemplate,
]);

/**
 * Migrates partial Events page settings by deep merging with defaults.
 * Properly handles nested objects like template, templateProps, commonProps,
 * mainMediaViewSettings, and desktopMainMediaSettings.
 *
 * If the template is a deprecated type (GridTemplate, ListTemplate), it will
 * be migrated to FramelessTemplate.
 */
export function doppeDtoInnerPageMigrateEventsPageFromDefaultValues(
    partialSettings: Partial<DoppeDtoInnerPage.Events> | undefined
): DoppeDtoInnerPage.Events {
    // Determine the user's template ID from stored settings
    const userTemplateId = partialSettings?.template?.templateId;
    const isTemplateSupported = userTemplateId !== undefined &&
        SUPPORTED_EVENT_LIST_TEMPLATE_IDS.has(userTemplateId);

    // Create defaults for the user's specific template (or Frameless if unsupported/missing)
    const effectiveTemplateId = isTemplateSupported
        ? userTemplateId
        : DoppeEventListPageTemplateId.FramelessTemplate;
    const defaults = doppeDtoInnerPageCreateEvents({templateId: effectiveTemplateId});

    if (!partialSettings) {
        return defaults;
    }

    const {
        template: templateOverride,
        templateProps: templatePropsOverride,
        commonProps: commonPropsOverride,
        mainMediaViewSettings: mainMediaViewSettingsOverride,
        desktopMainMediaSettings: desktopMainMediaSettingsOverride,
        ...restOverrides
    } = partialSettings;

    // If template is not supported, use defaults (which is FramelessTemplate)
    const migratedTemplate = isTemplateSupported ? {
        ...defaults.template,
        ...templateOverride
    } : defaults.template;

    const migratedTemplateProps = isTemplateSupported ? {
        ...defaults.templateProps,
        ...templatePropsOverride
    } : doppeEventListPageTemplatePropsCreateDefault(DoppeEventListPageTemplateId.FramelessTemplate);

    return {
        ...defaults,
        ...restOverrides,
        template: migratedTemplate,
        templateProps: migratedTemplateProps,
        commonProps: {
            ...defaults.commonProps,
            ...commonPropsOverride
        },
        mainMediaViewSettings: {
            ...defaults.mainMediaViewSettings,
            ...mainMediaViewSettingsOverride
        },
        desktopMainMediaSettings: desktopMainMediaSettingsOverride ?? defaults.desktopMainMediaSettings,
    };
}

/**
 * Supported service list page template IDs.
 * Used for migration to detect deprecated templates.
 */
const SUPPORTED_SERVICE_LIST_TEMPLATE_IDS = new Set<DoppeServiceListPageTemplateId>([
    DoppeServiceListPageTemplateId.FramelessTemplate,
    DoppeServiceListPageTemplateId.CardTemplate,
    DoppeServiceListPageTemplateId.OnImageTemplate,
    DoppeServiceListPageTemplateId.RowTemplate,
]);

/**
 * Migrates partial Services page settings by deep merging with defaults.
 * Properly handles nested objects like template, templateProps, commonProps,
 * mainMediaViewSettings, and desktopMainMediaSettings.
 *
 * If the template is a deprecated type (GridTemplate, ListTemplate), it will
 * be migrated to FramelessTemplate.
 */
export function doppeDtoInnerPageMigrateServicesPageFromDefaultValues(
    partialSettings: Partial<DoppeDtoInnerPage.Services> | undefined
): DoppeDtoInnerPage.Services {
    // Determine the user's template ID from stored settings
    const userTemplateId = partialSettings?.template?.templateId;
    const isTemplateSupported = userTemplateId !== undefined &&
        SUPPORTED_SERVICE_LIST_TEMPLATE_IDS.has(userTemplateId);

    // Create defaults for the user's specific template (or Frameless if unsupported/missing)
    const effectiveTemplateId = isTemplateSupported
        ? userTemplateId
        : DoppeServiceListPageTemplateId.FramelessTemplate;
    const defaults = doppeDtoInnerPageCreateDefaultServicesPage({templateId: effectiveTemplateId});

    if (!partialSettings) {
        return defaults;
    }

    const {
        template: templateOverride,
        templateProps: templatePropsOverride,
        commonProps: commonPropsOverride,
        mainMediaViewSettings: mainMediaViewSettingsOverride,
        desktopMainMediaSettings: desktopMainMediaSettingsOverride,
        ...restOverrides
    } = partialSettings;

    // If template is not supported, use defaults (which is FramelessTemplate)
    const migratedTemplate = isTemplateSupported ? {
        ...defaults.template,
        ...templateOverride
    } : defaults.template;

    // If template was migrated, also reset templateProps to defaults
    const migratedTemplateProps = isTemplateSupported ? {
        ...defaults.templateProps,
        ...templatePropsOverride
    } : defaults.templateProps;

    return {
        ...defaults,
        ...restOverrides,
        template: migratedTemplate,
        templateProps: migratedTemplateProps,
        commonProps: {
            ...defaults.commonProps,
            ...commonPropsOverride
        },
        mainMediaViewSettings: {
            ...defaults.mainMediaViewSettings,
            ...mainMediaViewSettingsOverride
        },
        desktopMainMediaSettings: desktopMainMediaSettingsOverride ?? defaults.desktopMainMediaSettings,
    };
}

/**
 * Migrates partial Product item page settings by deep merging with defaults.
 * Properly handles nested objects like template, templateProps, and commonProps.
 */
export function doppeDtoInnerPageMigrateProductPageFromDefaultValues(
    partialSettings: Partial<DoppeDtoInnerPage.Product> | undefined
): DoppeDtoInnerPage.Product {
    const defaults = doppeDtoInnerPageCreateProductPageSettings();

    if (!partialSettings) {
        return defaults;
    }

    const {
        template: templateOverride,
        templateProps: templatePropsOverride,
        commonProps: commonPropsOverride,
        ...restOverrides
    } = partialSettings;

    return {
        ...defaults,
        ...restOverrides,
        template: {
            ...defaults.template,
            ...templateOverride
        },
        templateProps: {
            ...defaults.templateProps,
            ...templatePropsOverride
        },
        commonProps: {
            ...defaults.commonProps,
            ...commonPropsOverride
        },
    };
}

/**
 * Migrates partial Digital Product item page settings by deep merging with defaults.
 */
export function doppeDtoInnerPageMigrateDigitalProductPageFromDefaultValues(
    partialSettings: Partial<DoppeDtoInnerPage.DigitalProduct> | undefined
): DoppeDtoInnerPage.DigitalProduct {
    const defaults = doppeDtoInnerPageCreateDigitalProductPageSettings();

    if (!partialSettings) {
        return defaults;
    }

    const {
        template: templateOverride,
        templateProps: templatePropsOverride,
        commonProps: commonPropsOverride,
        ...restOverrides
    } = partialSettings;

    return {
        ...defaults,
        ...restOverrides,
        template: {
            ...defaults.template,
            ...templateOverride
        },
        templateProps: {
            ...defaults.templateProps,
            ...templatePropsOverride
        },
        commonProps: {
            ...defaults.commonProps,
            ...commonPropsOverride
        },
    };
}

/**
 * Migrates partial Course item page settings by deep merging with defaults.
 * Properly handles nested objects like template, templateProps, and commonProps.
 */
export function doppeDtoInnerPageMigrateCoursePageFromDefaultValues(
    partialSettings: Partial<DoppeDtoInnerPage.Course> | undefined
): DoppeDtoInnerPage.Course {
    const defaults = doppeDtoInnerPageCreateCoursePageSettings();

    if (!partialSettings) {
        return defaults;
    }

    const {
        template: templateOverride,
        templateProps: templatePropsOverride,
        commonProps: commonPropsOverride,
        ...restOverrides
    } = partialSettings;

    return {
        ...defaults,
        ...restOverrides,
        template: {
            ...defaults.template,
            ...templateOverride
        },
        templateProps: {
            ...defaults.templateProps,
            ...templatePropsOverride
        },
        commonProps: {
            ...defaults.commonProps,
            ...commonPropsOverride
        },
    };
}

/**
 * Migrates partial Appointment item page settings by deep merging with defaults.
 * Properly handles nested objects like template, templateProps, and commonProps.
 */
export function doppeDtoInnerPageMigrateAppointmentPageFromDefaultValues(
    partialSettings: Partial<DoppeDtoInnerPage.Appointment> | undefined
): DoppeDtoInnerPage.Appointment {
    const defaults = doppeDtoInnerPageCreateAppointmentPageSettings();

    if (!partialSettings) {
        return defaults;
    }

    const {
        template: templateOverride,
        templateProps: templatePropsOverride,
        commonProps: commonPropsOverride,
        ...restOverrides
    } = partialSettings;

    return {
        ...defaults,
        ...restOverrides,
        template: {
            ...defaults.template,
            ...templateOverride
        },
        templateProps: {
            ...defaults.templateProps,
            ...templatePropsOverride
        },
        commonProps: {
            ...defaults.commonProps,
            ...commonPropsOverride
        },
    };
}

/**
 * Migrates partial Class item page settings by deep merging with defaults.
 * Properly handles nested objects like template, templateProps, and commonProps.
 */
export function doppeDtoInnerPageMigrateClassPageFromDefaultValues(
    partialSettings: Partial<DoppeDtoInnerPage.Class> | undefined
): DoppeDtoInnerPage.Class {
    const defaults = doppeDtoInnerPageCreateClassPageSettings();

    if (!partialSettings) {
        return defaults;
    }

    const {
        template: templateOverride,
        templateProps: templatePropsOverride,
        commonProps: commonPropsOverride,
        ...restOverrides
    } = partialSettings;

    return {
        ...defaults,
        ...restOverrides,
        template: {
            ...defaults.template,
            ...templateOverride
        },
        templateProps: {
            ...defaults.templateProps,
            ...templatePropsOverride
        },
        commonProps: {
            ...defaults.commonProps,
            ...commonPropsOverride
        },
    };
}

/**
 * Migrates partial Event item page settings by deep merging with defaults.
 * Properly handles nested objects like template, templateProps, and commonProps.
 */
export function doppeDtoInnerPageMigrateEventPageFromDefaultValues(
    partialSettings: Partial<DoppeDtoInnerPage.Event> | undefined
): DoppeDtoInnerPage.Event {
    const defaults = doppeDtoInnerPageCreateEventPageSettings();

    if (!partialSettings) {
        return defaults;
    }

    const {
        template: templateOverride,
        templateProps: templatePropsOverride,
        commonProps: commonPropsOverride,
        ...restOverrides
    } = partialSettings;

    return {
        ...defaults,
        ...restOverrides,
        template: {
            ...defaults.template,
            ...templateOverride
        },
        templateProps: {
            ...defaults.templateProps,
            ...templatePropsOverride
        },
        commonProps: {
            ...defaults.commonProps,
            ...commonPropsOverride
        },
    };
}

/**
 * Migrates partial TicketingEvent item page settings by deep merging with defaults.
 * Properly handles nested objects like template, templateProps, and commonProps.
 */
export function doppeDtoInnerPageMigrateTicketingEventPageFromDefaultValues(
    partialSettings: Partial<DoppeDtoInnerPage.TicketingEvent> | undefined
): DoppeDtoInnerPage.TicketingEvent {
    const defaults = doppeDtoInnerPageCreateTicketingEventPageSettings();

    if (!partialSettings) {
        return defaults;
    }

    const {
        template: templateOverride,
        templateProps: templatePropsOverride,
        commonProps: commonPropsOverride,
        ...restOverrides
    } = partialSettings;

    return {
        ...defaults,
        ...restOverrides,
        template: {
            ...defaults.template,
            ...templateOverride
        },
        templateProps: {
            ...defaults.templateProps,
            ...templatePropsOverride
        },
        commonProps: {
            ...defaults.commonProps,
            ...commonPropsOverride
        },
    };
}

/**
 * Migrates partial BlogPost item page settings by deep merging with defaults.
 * Properly handles nested objects like template, templateProps, and commonProps.
 */
export function doppeDtoInnerPageMigrateBlogPostPageFromDefaultValues(
    partialSettings: Partial<DoppeDtoInnerPage.BlogPost> | undefined
): DoppeDtoInnerPage.BlogPost {
    const defaults = doppeDtoInnerPageCreateBlogPostPageSettings();

    if (!partialSettings) {
        return defaults;
    }

    const {
        template: templateOverride,
        templateProps: templatePropsOverride,
        commonProps: commonPropsOverride,
        ...restOverrides
    } = partialSettings;

    return {
        ...defaults,
        ...restOverrides,
        template: {
            ...defaults.template,
            ...templateOverride
        },
        templateProps: {
            ...defaults.templateProps,
            ...templatePropsOverride
        },
        commonProps: {
            ...defaults.commonProps,
            ...commonPropsOverride
        },
    };
}

/**
 * Migrates partial Cart page settings by deep merging with defaults.
 * Ensures old cart pages (which had only `title: string`) get the full
 * DoppeDtoInnerPageHeaderSettings fields populated.
 */
export function doppeDtoInnerPageMigrateCartPageFromDefaultValues(
    partialSettings: Partial<DoppeDtoInnerPage.Cart> | undefined
): DoppeDtoInnerPage.Cart {
    const defaults = doppeDtoInnerPageCreateDefaultCartPage();

    if (!partialSettings) {
        return defaults;
    }

    const {
        mainMediaViewSettings: mainMediaViewSettingsOverride,
        desktopMainMediaSettings: desktopMainMediaSettingsOverride,
        ...restOverrides
    } = partialSettings;

    return {
        ...defaults,
        ...restOverrides,
        mainMediaViewSettings: {
            ...defaults.mainMediaViewSettings,
            ...mainMediaViewSettingsOverride
        },
        desktopMainMediaSettings: desktopMainMediaSettingsOverride ?? defaults.desktopMainMediaSettings,
    };
}

/**
 * Checks if a page is a vertical page (list pages like Products/Events/etc. or Cart)
 * by looking up its data in the vertical pages collection.
 */
export function bymoPageIsVerticalPage(
    pageId: string,
    bymoPageProps: BymoPageProps<any>
): boolean {
    if (pageId === DoppeDtoInnerPageKnownIds.Home) {
        return false;
    } else if (pageId === DoppeDtoInnerPageKnownIds.Cart) {
        return true;
    } else {
        const page = bymoPageGetInnerVerticalPages(bymoPageProps)
            .find(p => p.id === pageId);
        return !!page && doppeDtoInnerPageIsVerticalListType(page.type);
    }
}

/**
 * Checks if a page is a vertical ITEM page (e.g., Product page) by looking up its data in innerPages
 */
export function bymoPageIsVerticalItemPage(
    pageId: string,
    bymoPageProps: BymoPageProps<any>
): boolean {
    if (pageId === DoppeDtoInnerPageKnownIds.Home) {
        return false;
    }

    const page = bymoPageGetInnerVerticalItemPages(bymoPageProps)
        .find(p => p.id === pageId);
    return !!page?.type && doppeDtoInnerPageIsVerticalItemType(page.type);
}

/**
 * Derives the vertical page type from a WixTpaAppId
 */
export function bymoPageGetVerticalPageTypeFromAppId(verticalPageType: DoppeDtoInnerPageType): DoppeHtmlSitePagesType | null {
    switch (verticalPageType) {
        case DoppeDtoInnerPageType.Products:
            return DoppeHtmlSitePagesType.Store;
        case DoppeDtoInnerPageType.BlogPosts:
            return DoppeHtmlSitePagesType.Blog;
        case DoppeDtoInnerPageType.Events:
            return DoppeHtmlSitePagesType.Events;
        case DoppeDtoInnerPageType.Services:
            return DoppeHtmlSitePagesType.BookingServices;
        default:
            return null;
    }
}

/**
 * Gets a page by ID from innerPages (includes both content pages and vertical pages)
 */
export function bymoPageGetInnerNavigatablePageById(
    pageId: DoppeDtoInnerPage.Id,
    bymoPageProps: BymoPageProps<any>,
    options?: {
        withVerticalItemPages?: boolean;
    }
): DoppeDtoInnerPage | null {
    return bymoPageGetInnerNavigatablePages(bymoPageProps, options).find(p => p.id === pageId) ?? null;
}

/**
 * Gets all pages for navigation in the correct order based on navigationSettings.menuItems
 * This is the main function that should be used by navigation components
 * Only returns page-type menu items that are visible, filtered and with resolved titles
 */
export function bymoPageGetInnerNavigatablePages(
    bymoPageProps: BymoPageProps,
    options?: {
        withVerticalItemPages?: boolean;
    }
): DoppeDtoInnerPage[] {
    const {withVerticalItemPages = false} = options ?? {};

    const pages = [...bymoPagePropsGetInnerContentPages(bymoPageProps), ...bymoPageGetInnerVerticalPages(bymoPageProps)];

    if (withVerticalItemPages) {
        pages.push(...bymoPageGetInnerVerticalItemPages(bymoPageProps));
    }

    return pages;
}

export function bymoPagePropsGetInnerContentPages(bymoPageProps: BymoPageProps) {
    return bymoPageProps.innerPages;
}

/**
 * Gets all pages for navigation in the correct order based on navigationSettings.menuItems
 * This is the main function that should be used by navigation components
 * Only returns page-type menu items that are visible, filtered and with resolved titles
 */
export function bymoPageGetInnerContentPages(
    bymoPageProps: BymoPageProps
): DoppeDtoInnerPage[] {
    return bymoPageProps.innerPages;
}

/**
 * Gets all pages for navigation in the correct order based on navigationSettings.menuItems
 * This is the main function that should be used by navigation components
 * Only returns page-type menu items that are visible, filtered and with resolved titles
 */
export function bymoPageGetInnerVerticalPages(
    bymoPageProps: BymoPageProps
): DoppeDtoInnerPage[] {
    return arrayRemoveNullishValues([bymoPageProps.defaultBlogPostsPage, bymoPageProps.defaultStoreProductsPage, bymoPageProps.defaultEventsPage, bymoPageProps.defaultServicesPage, bymoPageProps.defaultCartPage]);
}

export function bymoPageGetInnerVerticalItemPages(
    bymoPageProps: BymoPageProps
): DoppeDtoInnerPage[] {
    return arrayRemoveNullishValues([
        bymoPageProps.defaultProductPage,
        bymoPageProps.defaultDigitalProductPage,
        bymoPageProps.defaultCoursePage,
        bymoPageProps.defaultAppointmentPage,
        bymoPageProps.defaultClassPage,
        bymoPageProps.defaultEventPage,
        bymoPageProps.defaultTicketingEventPage,
        bymoPageProps.defaultBlogPostPage
    ]);
}

export function bymoPageGetBlogPages(
    bymoPageProps: BymoPageProps
): DoppeDtoInnerPage[] {
    return [
        bymoPageProps.defaultBlogPostsPage,
        bymoPageProps.defaultBlogPostPage
    ];
}

export function bymoPageGetStorePages(
    bymoPageProps: BymoPageProps
): DoppeDtoInnerPage[] {
    return [
        bymoPageProps.defaultStoreProductsPage,
        bymoPageProps.defaultProductPage,
        bymoPageProps.defaultDigitalProductPage,
        bymoPageProps.defaultCartPage,
    ];
}

export function bymoPageGetEventsPages(
    bymoPageProps: BymoPageProps
): DoppeDtoInnerPage[] {
    return [
        bymoPageProps.defaultEventsPage,
        bymoPageProps.defaultEventPage,
        bymoPageProps.defaultTicketingEventPage
    ];
}

export function bymoPageGetServicesPages(
    bymoPageProps: BymoPageProps
): DoppeDtoInnerPage[] {
    return arrayRemoveNullishValues([
        bymoPageProps.defaultServicesPage,
        bymoPageProps.defaultCoursePage,
        bymoPageProps.defaultAppointmentPage,
        bymoPageProps.defaultClassPage
    ]);
}